import { List } from "@clipboard-health/ui-components";
import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction";
import { useHistory } from "react-router-dom";

import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "../../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { type BlockingRequirementsContext } from "../../Bookability/Documents/types";
import { ShiftBottomSheetBookabilityCardsContainer } from "../../BottomSheet/BookabilityCardsContainer";
import { type ShiftType } from "../../constants";
import { type Window } from "../../Open/types";
import { type ModalWorkplaceData, type WindowRestriction } from "../../types";
import { ShiftWorkplaceListItem } from "../../WorkplaceListItem";
import { type BundleShiftPay, type OpenBundleShift } from "../types";
import { ShiftBundleShiftInfoListItem } from "./ShiftInfoListItem";

interface ShiftBundleBottomSheetContentProps {
  shiftId: string;
  shiftType: ShiftType;
  shiftQualification: string;
  shiftStartIso: string;
  workplace: ModalWorkplaceData;
  bookabilityStatus: BookabilityStatusItem;
  blockingRequirementsContext?: BlockingRequirementsContext;
  hasHoursRestrictionConflict: boolean;
  shiftFailedLoading: boolean;
  hasMarkedInterest?: boolean;
  restrictedToWindow?: WindowRestriction;
  isIndependentContractorBadgesRequired?: boolean;
  window?: Window;
  sortedBundleShifts: OpenBundleShift[];
  bundleShiftPays?: Map<string, BundleShiftPay>;
}

export function ShiftBundleBottomSheetContent(props: ShiftBundleBottomSheetContentProps) {
  const {
    shiftId,
    shiftType,
    shiftQualification,
    shiftStartIso,
    workplace,
    bookabilityStatus,
    blockingRequirementsContext,
    hasHoursRestrictionConflict,
    shiftFailedLoading,
    hasMarkedInterest,
    restrictedToWindow,
    isIndependentContractorBadgesRequired,
    window,
    sortedBundleShifts,
    bundleShiftPays,
  } = props;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();

  const workplaceTimezone = workplace.attributes.location.timezone;

  return (
    <Box sx={{ overflowY: "auto" }}>
      <ShiftBottomSheetBookabilityCardsContainer
        shiftId={shiftId}
        shiftType={shiftType}
        shiftQualification={shiftQualification}
        shiftStartIso={shiftStartIso}
        workplace={workplace}
        bookabilityStatus={bookabilityStatus}
        blockingRequirementsContext={blockingRequirementsContext}
        hasHoursRestrictionConflict={hasHoursRestrictionConflict}
        shiftFailedLoading={shiftFailedLoading}
        hasMarkedInterest={hasMarkedInterest}
        restrictedToWindow={restrictedToWindow}
        isIndependentContractorBadgesRequired={isIndependentContractorBadgesRequired}
        window={window}
      />

      <List sx={{ paddingX: 6 }}>
        <ShiftWorkplaceListItem
          workplaceId={workplace.id}
          workplaceName={workplace.attributes.name}
          workplaceType={workplace.attributes.type ?? ""}
          workplaceLocation={workplace.attributes.location}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.VIEWED_FACILITY_PROFILE, {
              workplaceId: workplace.id,
              trigger: "Shift Bundle Bottom Sheet",
            });

            history.push(
              generateModalPath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
                workplaceId: workplace.id,
              })
            );
          }}
        />
      </List>

      <List sx={{ paddingX: 6 }}>
        {sortedBundleShifts.map((shift) => (
          <ShiftBundleShiftInfoListItem
            key={shift.id}
            timeSlot={shift.attributes.timeSlot}
            startIso={shift.attributes.start}
            endIso={shift.attributes.end}
            payInMinorUnits={bundleShiftPays?.get(shift.id)?.totalPayInMinorUnits}
            workplaceTimezone={workplaceTimezone}
          />
        ))}
      </List>
    </Box>
  );
}
